/**
 * Editor enriquecido del blog.
 *
 * Envuelve TipTap, pero la mayor parte de la lógica está fuera: las extensiones
 * viven en `extensions.ts`, el pegado en `paste.ts` y la estructura en
 * `structure.ts`. Este archivo solo conecta la interfaz con el documento.
 *
 * Tres decisiones que conviene no revertir sin pensarlo:
 *
 * 1. **El H1 no existe como opción.** El título de la publicación es el H1 de
 *    la página, así que el StarterKit se limita a H2 y H3.
 *
 * 2. **El HTML pegado pasa por un transformador** antes de entrar al
 *    documento. TipTap trae lo que el portapapeles entrega, incluidos estilos
 *    inline y encabezados H1; el renderer los descartaría y el autor perdería
 *    formato que sí tenía a la vista.
 *
 * 3. **El FAQ no se escribe dentro del documento.** Es un node atómico que se
 *    edita con `FaqEditor` y se reinserta al guardar (`withFaqEntries`), igual
 *    que al leer (`splitFaq`). Si entrara en el cuerpo, el autor vería cajas
 *    vacías en medio del artículo.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import { RICH_CONTENT_CSS, docAsPlainSource, proposeStructure, proposalToDoc } from "@vimos/rich-content";
import type { FaqEntry, RichDoc } from "@vimos/rich-content";
import { isSafeImageTarget, isSafeLinkTarget } from "./extensions";
import { buildEditorExtensions } from "./setup";
import { normalizePaste } from "./paste";
import { FaqEditor } from "./FaqEditor";
import { MediaPicker } from "../../media/MediaPicker";

interface RichTextEditorProps {
  doc: RichDoc;
  faq: FaqEntry[];
  onChange: (doc: RichDoc, faq: FaqEntry[]) => void;
}

export function RichTextEditor({ doc, faq, onChange }: RichTextEditorProps) {
  const [notes, setNotes] = useState<string[]>([]);
  const [showStructure, setShowStructure] = useState(false);

  // Instancia viva para usarla dentro de los handlers de `editorProps`, que se
  // definen antes de que `useEditor` devuelva el editor.
  const editorRef = useRef<Editor | null>(null);
  // Último doc emitido por el propio editor: permite distinguir "cambió el
  // usuario" de "cambió el padre" y evitar un bucle de setContent.
  const emittedRef = useRef<string>("");
  const faqRef = useRef(faq);
  faqRef.current = faq;

  const editor = useEditor({
    extensions: buildEditorExtensions(),
    content: doc,
    editorProps: {
      attributes: { class: "rc-prose min-h-[420px] focus:outline-none" },
      /**
       * Pegado controlado: el HTML se sanea antes de insertarse.
       *
       * `event.clipboardData` puede ser null si el navegador no expone HTML
       * (por ejemplo al pegar desde una app nativa), y en ese caso se devuelve
       * `false` para que TipTap use su comportamiento por defecto.
       */
      handlePaste: (_view, event) => {
        const clipboard = event.clipboardData;
        const instance = editorRef.current;
        if (!clipboard || !instance) return false;

        const normalized = normalizePaste({
          html: clipboard.getData("text/html"),
          text: clipboard.getData("text/plain"),
        });
        if (!normalized) return false;

        if (normalized.html) {
          instance.commands.insertContent(normalized.html);
          setNotes(normalized.notes);
          return true;
        }

        // Solo texto con estructura reconocible: se reconstruye desde ahí.
        if (normalized.structuredText) {
          const proposal = proposeStructure(normalized.structuredText);
          const next = proposalToDoc(proposal.blocks);
          instance.commands.insertContent(next.content);
          setNotes(normalized.notes);
          return true;
        }

        return false;
      },
    },
    onCreate: ({ editor: instance }) => {
      editorRef.current = instance;
    },
    onUpdate: ({ editor: instance }) => {
      const next = instance.getJSON() as RichDoc;
      emittedRef.current = JSON.stringify(next);
      onChange(next, faqRef.current);
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  /**
   * Sincroniza el documento cuando cambia desde fuera: al cargar una
   * publicación existente, o al aplicar una propuesta de estructura.
   */
  useEffect(() => {
    if (!editor) return;
    const incoming = JSON.stringify(doc);
    if (incoming === emittedRef.current) return;
    if (JSON.stringify(editor.getJSON()) === incoming) return;
    emittedRef.current = incoming;
    editor.commands.setContent(doc, { emitUpdate: false });
  }, [editor, doc]);

  const structureProposal = useMemo(
    // Se calcula al abrir el panel, no en cada tecla: el editor es la fuente y
    // el contenido cambia mucho más que la apertura del panel.
    () => (editor ? proposeStructure(docAsPlainSource(editor.getJSON() as RichDoc)) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, showStructure],
  );

  const plainSource = useMemo(() => docAsPlainSource(doc), [doc]);

  const insertImage = useCallback(
    (url: string) => {
      if (!editor || !isSafeImageTarget(url)) return;
      editor.chain().focus().setImage({ src: url }).run();
    },
    [editor],
  );

  const applyStructure = useCallback(() => {
    if (!editor || !structureProposal || structureProposal.blocks.length === 0) return;
    const next = proposalToDoc(structureProposal.blocks);
    editor.commands.setContent(next);
    setNotes(["Se reordeno el contenido en bloques. Revisa que las decisiones de estructura te sirvan."]);
    setShowStructure(false);
  }, [editor, structureProposal]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const previous = (editor.getAttributes("link").href as string | undefined) ?? "";
    const value = window.prompt("URL del enlace", previous || "https://");
    if (value === null) return;

    const trimmed = value.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    // Se valida en el editor y no solo al renderizar: avisar ahora evita
    // descubrir el problema en la vista previa.
    if (!isSafeLinkTarget(trimmed)) {
      setNotes(["El enlace no se aplico: solo se permiten URLs http, https, mailto, tel o rutas internas."]);
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: trimmed }).run();
  }, [editor]);

  const handleFaqChange = useCallback((items: FaqEntry[]) => onChange(doc, items), [doc, onChange]);

  if (!editor) {
    return <p className="text-mist mt-5 text-sm">Preparando el editor...</p>;
  }

  return (
    <div className="mt-5">
      <style>{RICH_CONTENT_CSS}</style>

      <div className="border-sand/60 flex flex-wrap items-center gap-1.5 rounded-t-md border bg-[#f7f7f4] p-2">
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="B"
          title="Negrita (Ctrl+B)"
          bold
        />
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="I"
          title="Cursiva (Ctrl+I)"
          italic
        />
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label="S"
          title="Subrayado (Ctrl+U)"
          underline
        />
        <ToolbarButton
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          label="Tachado"
          title="Tachado (Ctrl+Shift+X)"
        />

        <span className="bg-sand mx-1 h-6 w-px" />

        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          label="H2"
          title="Subtitulo principal"
        />
        <ToolbarButton
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          label="H3"
          title="Subtitulo secundario"
        />

        <span className="bg-sand mx-1 h-6 w-px" />

        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="•"
          title="Lista con vinetas"
        />
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="1."
          title="Lista numerada"
        />
        <ToolbarButton
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label="❝"
          title="Cita"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          label="—"
          title="Separador"
        />
        <ToolbarButton
          active={editor.isActive("codeBlock")}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          label="</>"
          title="Bloque de codigo"
        />
        <ToolbarButton
          active={editor.isActive("table")}
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          label="▦"
          title="Tabla"
        />

        <span className="bg-sand mx-1 h-6 w-px" />

        <ToolbarButton
          active={editor.isActive("link")}
          onClick={applyLink}
          label="Enlace"
          title="Insertar enlace (Ctrl+K)"
        />
        <ToolbarButton
          onClick={() => setShowStructure((value) => !value)}
          label="Estructurar"
          title="Proponer estructura del contenido"
          active={showStructure}
        />
      </div>

      <div className="border-sand/60 bg-paper rounded-b-md border-x border-b px-5 py-4">
        <EditorContent editor={editor} />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <MediaPicker
          label="Insertar imagen en el contenido"
          value=""
          onChange={(url) => {
            if (typeof url === "string") insertImage(url);
          }}
        />
        <p className="text-mist self-center text-xs">
          Las imagenes arrastradas desde el escritorio no se suben aqui: usa el selector para no dejar subidas
          a medias.
        </p>
      </div>

      <FaqEditor items={faq} sourceText={plainSource} onChange={handleFaqChange} />

      {showStructure && structureProposal && (
        <div className="border-sand/50 mt-4 rounded-md border bg-[#f7f7f4] p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-body text-ink text-sm font-semibold">Estructura detectada</p>
              <p className="text-mist mt-0.5 text-xs">
                {structureProposal.summary.headings} subtitulo(s), {structureProposal.summary.lists} lista(s),{" "}
                {structureProposal.summary.paragraphs} parrafo(s). Nada se aplica hasta que lo confirmes.
              </p>
            </div>
            <button
              type="button"
              onClick={applyStructure}
              className="bg-navy hover:bg-navy-700 rounded-full px-4 py-1.5 text-xs font-semibold text-white"
            >
              Aplicar propuesta
            </button>
          </div>
          <ul className="text-mist mt-3 space-y-1.5 text-xs">
            {structureProposal.blocks.slice(0, 12).map((block, index) => (
              <li key={index} className="flex gap-2">
                <span className="text-gold-hover w-20 shrink-0 font-mono uppercase">{block.kind}</span>
                <span className="text-ink truncate">{block.text.slice(0, 90)}</span>
              </li>
            ))}
            {structureProposal.blocks.length > 12 && (
              <li>+{structureProposal.blocks.length - 12} bloque(s) mas</li>
            )}
          </ul>
        </div>
      )}

      {notes.length > 0 && (
        <ul
          role="status"
          className="border-gold/40 bg-gold-subtle text-ink mt-3 space-y-1 rounded-md border px-4 py-3 text-xs"
        >
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
          <li>
            <button type="button" onClick={() => setNotes([])} className="underline">
              Entendido
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

interface ToolbarButtonProps {
  /** Botones de acción (separador, insertar tabla) no tienen estado activo. */
  active?: boolean;
  onClick: () => void;
  label: string;
  title: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

function ToolbarButton({
  active = false,
  onClick,
  label,
  title,
  bold,
  italic,
  underline,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={[
        "font-body min-w-9 rounded-md px-2 py-1.5 text-sm transition-colors",
        bold ? "font-bold" : "",
        italic ? "italic" : "",
        underline ? "underline" : "",
        active ? "bg-navy text-white" : "text-navy hover:bg-sand/40",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label}
    </button>
  );
}
